import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  InputSignal,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { WSPlayer } from '../../models/WS-player.model';
import { WSUser } from '../../models/WS-user.model';
import { WSBroadcastDataMessage } from '../../models/WSMessages/WS-broadcast-data-message.model';
import { WebSocketService } from '../../services/websocket-service';
import { PlayerItem } from '../player-item/player-item';
import { DisplayAnimations } from '../../directives/display-animations';
import { Icon } from '../icon/icon';

export type RankEntry = {
  player: WSPlayer;
  position: number;
};

@Component({
  selector: 'app-rank',
  imports: [PlayerItem, Icon],
  templateUrl: './rank.html',
  styleUrl: './rank.scss',
  hostDirectives: [DisplayAnimations],
})
export class Rank {
  private webSocketService: WebSocketService = inject(WebSocketService);
  private rankList: Signal<ElementRef<HTMLUListElement> | undefined> = viewChild('rankList');

  private readonly RANK_ANIMATION_DURATION_MS: number = 350;
  private readonly CLIENT_FOLLOWERS_VISIBLE: number = 1;

  private resizeObserver?: ResizeObserver;
  private previousItemRects: Map<string, DOMRect> = new Map<string, DOMRect>();
  private animationFrameId?: number;
  private visibleCount: WritableSignal<number> = signal(Number.MAX_SAFE_INTEGER);

  players: InputSignal<WSPlayer[]> = input.required();

  private rankedPlayers: Signal<WSPlayer[]> = computed<WSPlayer[]>(() => {
    const players: WSPlayer[] = this.players();
    const client: WSUser | undefined = this.webSocketService.client();
    const clientData: WSBroadcastDataMessage['data'] | undefined =
      this.webSocketService.broadcastData()?.data;
    if (!client || !clientData) return players;

    const isClient = (player: WSPlayer): boolean =>
      player.user.username === client.username && player.user.avatarUrl === client.avatarUrl;

    const clientPlayer: WSPlayer = players.find(isClient) ?? {
      user: client,
      data: clientData,
      finalTime: undefined,
      isSlipstreamActive: false,
    };
    const others: WSPlayer[] = players
      .filter((player: WSPlayer) => !isClient(player))
      .sort((a: WSPlayer, b: WSPlayer) => b.data.distance - a.data.distance);

    const clientIndex: number = others.findIndex(
      (player: WSPlayer) => player.data.distance < 0,
    );
    const insertIndex: number = clientIndex < 0 ? others.length : clientIndex;

    return [...others.slice(0, insertIndex), clientPlayer, ...others.slice(insertIndex)];
  });

  playersCount: Signal<number> = computed(() => this.rankedPlayers().length);

  private clientIndex: Signal<number> = computed<number>(() => {
    const client: WSUser | undefined = this.webSocketService.client();
    if (!client) return -1;
    return this.rankedPlayers().findIndex(
      (player: WSPlayer) =>
        player.user.username === client.username && player.user.avatarUrl === client.avatarUrl,
    );
  });

  visibleEntries: Signal<RankEntry[]> = computed<RankEntry[]>(() => {
    const players: WSPlayer[] = this.rankedPlayers();
    const visibleCount: number = Math.min(this.visibleCount(), players.length);
    if (visibleCount <= 0) return [];

    const clientIndex: number = this.clientIndex();
    const followers: number = Math.min(this.CLIENT_FOLLOWERS_VISIBLE, visibleCount - 1);
    const lastVisibleIndex: number =
      clientIndex < 0 ? visibleCount - 1 : Math.min(players.length - 1, clientIndex + followers);

    const end: number = Math.max(visibleCount, lastVisibleIndex + 1);
    const start: number = end - visibleCount;

    return players
      .slice(start, end)
      .map((player: WSPlayer, index: number) => ({ player, position: start + index }));
  });

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private injector: Injector,
  ) {
    effect(() => {
      this.rankedPlayers();
      afterNextRender(
        () => {
          this.updateVisibleCount();
          afterNextRender(() => this.animateRankChanges(), { injector: this.injector });
        },
        { injector: this.injector },
      );
    });
  }

  private updateVisibleCount(): void {
    const list: HTMLUListElement | undefined = this.rankList()?.nativeElement;
    const firstItem: HTMLLIElement | null | undefined = list?.querySelector('li');
    if (!list || !firstItem) {
      this.setVisibleCount(this.rankedPlayers().length);
      return;
    }

    const listStyle: CSSStyleDeclaration = getComputedStyle(list);
    const gap: number = parseFloat(listStyle.rowGap || listStyle.gap) || 0;
    const availableHeight: number = list.clientHeight;
    const itemHeight: number = firstItem.offsetHeight;

    if (availableHeight <= 0 || itemHeight <= 0) {
      this.setVisibleCount(this.rankedPlayers().length);
      return;
    }

    const visibleCount: number = Math.floor((availableHeight + gap) / (itemHeight + gap));
    this.setVisibleCount(visibleCount);
  }

  private setVisibleCount(count: number): void {
    const visibleCount: number = Math.min(this.rankedPlayers().length, Math.max(0, count));
    if (this.visibleCount() !== visibleCount) this.visibleCount.set(visibleCount);
  }

  ngAfterViewInit(): void {
    const list: HTMLUListElement | undefined = this.rankList()?.nativeElement;
    if (!list) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateVisibleCount();
      afterNextRender(() => this.saveCurrentItemRects(), { injector: this.injector });
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
    this.resizeObserver.observe(list);
    this.updateVisibleCount();
    afterNextRender(() => this.saveCurrentItemRects(), { injector: this.injector });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.animationFrameId !== undefined) window.cancelAnimationFrame(this.animationFrameId);
  }

  private animateRankChanges(): void {
    const items: HTMLLIElement[] = this.rankItems();
    if (items.length === 0) {
      this.previousItemRects.clear();
      return;
    }

    for (const item of items) {
      const playerName: string | undefined = item.dataset['rankPlayer'];
      if (!playerName) continue;

      const previousRect: DOMRect | undefined = this.previousItemRects.get(playerName);
      if (!previousRect) continue;

      const currentRect: DOMRect = item.getBoundingClientRect();
      const deltaY: number = previousRect.top - currentRect.top;
      if (deltaY === 0) continue;

      item.style.transition = 'none';
      item.style.transform = `translateY(${deltaY}px)`;
    }

    if (this.animationFrameId !== undefined) window.cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = window.requestAnimationFrame(() => {
      for (const item of items) {
        item.style.transition = `transform ${this.RANK_ANIMATION_DURATION_MS}ms ease`;
        item.style.transform = '';
      }

      window.setTimeout(() => {
        for (const item of items) {
          item.style.transition = '';
          item.style.transform = '';
        }
        this.saveCurrentItemRects();
      }, this.RANK_ANIMATION_DURATION_MS);
    });
  }

  private saveCurrentItemRects(): void {
    this.previousItemRects = new Map<string, DOMRect>(
      this.rankItems()
        .map((item: HTMLLIElement): [string | undefined, DOMRect] => [
          item.dataset['rankPlayer'],
          item.getBoundingClientRect(),
        ])
        .filter((entry: [string | undefined, DOMRect]): entry is [string, DOMRect] =>
          Boolean(entry[0]),
        ),
    );
  }

  private rankItems(): HTMLLIElement[] {
    return Array.from(this.rankList()?.nativeElement.querySelectorAll('li') ?? []);
  }
}
